import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { Table, useLocalizeMessage, useToastNotification, Select, Button, SearchField, } from 'libs.nucleus';
import { useDateTime } from 'libs.react';
import DateRangeSelector from './components/daterange_selector.component';
import { LogEvents } from '../../constants/log_events.constants';
import { getApiInstance } from '../../helpers/api.helpers';
import { useAxonAccountContext } from '../../hooks/use_axon_account_context';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosEventLogger } from '../../hooks/use_lumos_event_logger';
import { useSiteContext } from '../../hooks/use_site_context';
const USER_TYPES = {
    CLINRO: 'clinro', // Site
    EPRO: 'epro', // Participant
    OBSRO: 'obsro', // Caregiver
};
export const ParticipantResponses = ({ participantId }) => {
    const translate = useLocalizeMessage();
    const navigate = useNavigate();
    const { environment } = useCurrentEnvironment();
    const site = useSiteContext();
    const account = useAxonAccountContext();
    const { parseDateTimeShort } = useDateTime();
    const { addNotification } = useToastNotification();
    const { logLumosEvent } = useLumosEventLogger();
    const match = useMatch('/site/study/:studyId/participants/:participantId');
    const studyId = match?.params.studyId;
    const [searchTerm, setSearchTerm] = useState({
        value: '',
        debouncedValue: '',
    });
    const [selectedDateRange, setSelectedDateRange] = useState({
        from: undefined,
        to: undefined,
    });
    const [selectedUserType, setSelectedUserType] = useState({
        label: translate('label.participant_responses.dropdown_all'),
        value: 'All',
    });
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: 10,
    });
    const searchTimeoutRef = useRef();
    const logEvent = (event, payload = {}) => {
        const payloadToSend = {
            participantId,
            ...payload,
        };
        logLumosEvent(event, payloadToSend);
    };
    const getParticipantResponses = async () => {
        if (!environment || !site) {
            return {
                data: [],
                pagination: {
                    totalCount: 0,
                    pageSize: 0,
                    currentPage: 0,
                    totalPages: 0,
                },
                hasMore: false,
            };
        }
        const whereClause = {
            participant_id: participantId,
        };
        const eventPayload = {};
        // Add user type filter to params
        if (selectedUserType && selectedUserType.value && !['All', ''].includes(selectedUserType.value)) {
            whereClause.activity_type = selectedUserType.value;
            eventPayload['filter_userType'] = selectedUserType.value;
        }
        // Add date range filter to params
        if (selectedDateRange.from || selectedDateRange.to) {
            const nextDayDate = selectedDateRange.to?.add({ days: 1 });
            const isoFromDate = new Date(selectedDateRange.from?.toString() || '').toISOString();
            const isoToDate = new Date(nextDayDate?.toString() || '').toISOString();
            whereClause.completion_date = {
                $gte: isoFromDate,
                $lt: isoToDate,
            };
            eventPayload['filter_dateRange'] = [isoFromDate, isoToDate];
        }
        // Add search term filter to where clause
        if (searchTerm.debouncedValue.trim() !== '') {
            whereClause.activity_name = searchTerm.debouncedValue.trim();
            eventPayload['filter_searchTerm'] = searchTerm.debouncedValue;
        }
        const response = await getApiInstance(environment).get(`routes/c_sites/${site._id}/c_all_participant_responses`, {
            params: {
                where: JSON.stringify(whereClause),
                limit: pagination.pageSize,
                skip: pagination.pageIndex * pagination.pageSize,
            },
        });
        logEvent(LogEvents.PARTICIPANT_RESPONSES_FETCHED, eventPayload);
        return {
            data: response?.data?.data || [],
            hasMore: response?.data?.hasMore || false,
            pagination: response?.data?.pagination,
        };
    };
    const { data: responseData, isLoading, isError, } = useQuery({
        queryKey: [
            'participantResponses',
            account._id,
            site?._id,
            participantId,
            searchTerm.debouncedValue,
            selectedUserType,
            selectedDateRange,
            pagination.pageIndex,
            pagination.pageSize,
        ],
        queryFn: getParticipantResponses,
        enabled: !!environment && !!account._id && !!site?._id && !!participantId,
    });
    const responses = responseData?.data || [];
    const totalCount = responseData?.pagination.total_count || 0;
    const pageCount = Math.ceil(totalCount / pagination.pageSize);
    const getUserType = (response) => {
        if (response?.activity_type) {
            if (response.activity_type === USER_TYPES.OBSRO) {
                return 'Caregiver';
            }
            if (response.activity_type === USER_TYPES.EPRO) {
                return 'Participant';
            }
        }
        return 'Site';
    };
    const handlePaginationChange = (paginationState) => {
        setPagination(paginationState);
    };
    const handleSearchChange = (value) => {
        setSearchTerm((p) => ({ ...p, value: value }));
    };
    const handleSearchClear = () => {
        setSearchTerm((p) => ({ ...p, value: '' }));
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    const handleSelectUserType = (selected) => {
        setSelectedUserType(selected);
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    const handleDateRangeChange = (newDateRange) => {
        setSelectedDateRange((prev) => ({
            ...prev,
            ...newDateRange,
        }));
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    const handleResponseClick = (response) => {
        navigate(`/site/study/${studyId}/participants/${participantId}/response/${response._id}`);
    };
    const handleClearFilters = () => {
        setSelectedDateRange({ from: undefined, to: undefined });
        setSelectedUserType({ label: translate('label.participant_responses.dropdown_all'), value: 'All' });
        setSearchTerm({
            value: '',
            debouncedValue: '',
        });
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    };
    useEffect(() => {
        logEvent(LogEvents.PARTICIPANT_RESPONSES_TAB_LOADED);
    }, []);
    useEffect(() => {
        if (isError) {
            addNotification({
                title: '',
                subtitle: translate('error.participant_responses.fetch'),
                type: 'error',
            });
        }
    }, [isError]);
    // Debounce the search term
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }
        searchTimeoutRef.current = setTimeout(() => {
            setSearchTerm((p) => ({ ...p, debouncedValue: p.value }));
        }, 300);
        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
                searchTimeoutRef.current = undefined;
            }
        };
    }, [searchTerm.value]);
    const isResetFiltersDisabled = useMemo(() => {
        const isSearchTermEmpty = searchTerm.value === '' || searchTerm.value === undefined;
        const isUserTypeAll = selectedUserType.value === 'All';
        const isDateRangeEmpty = selectedDateRange.from === undefined && selectedDateRange.to === undefined;
        return isSearchTermEmpty && isUserTypeAll && isDateRangeEmpty;
    }, [selectedDateRange, selectedUserType, searchTerm]);
    const userTypeOptions = [
        { label: translate('label.participant_responses.dropdown_all'), value: 'All' },
        { label: translate('label.participant_responses.dropdown_participant'), value: USER_TYPES.EPRO },
        { label: translate('label.participant_responses.dropdown_site'), value: USER_TYPES.CLINRO },
        { label: translate('label.participant_responses.dropdown_caregiver'), value: USER_TYPES.OBSRO },
    ];
    const columns = [
        {
            header: translate('label.participant_responses.activity_name'),
            accessorKey: 'activity_name',
            cell: (context) => {
                const row = context.row.original;
                return (_jsx("span", { className: 'text-primary-600 cursor-pointer hover:underline', onClick: () => handleResponseClick(row), children: row.activity_name }));
            },
        },
        {
            header: translate('generic.id'),
            accessorKey: 'task_response_c_number',
            cell: (context) => {
                const row = context.row.original;
                return row.task_response_c_number;
            },
        },
        {
            header: translate('label.participant_responses.user_type'),
            accessorKey: 'activity_type',
            cell: (context) => {
                const row = context.row.original;
                return getUserType(row);
            },
        },
        {
            header: translate('label.participant_responses.visit'),
            accessorKey: 'schedule',
            cell: (context) => {
                const row = context.row.original;
                return row.schedule ?? '';
            },
        },
        {
            header: translate('label.participant_responses.activity_completion_date'),
            accessorKey: 'created_date',
            cell: (context) => {
                const row = context.row.original;
                return parseDateTimeShort(row.created_date);
            },
        },
    ];
    return (_jsx("div", { className: 'flex flex-col gap-6', children: _jsx(Table, { data: responses || [], columns: columns, isLoading: isLoading, labelNoResults: translate('info.participant_responses.empty'), hasPagination: true, manualPagination: true, manualFiltering: true, pageCount: pageCount, manualPaginationState: pagination, onPaginationChange: handlePaginationChange, paginationNavigationScreenReaderLabel: translate('Navigate through response pages'), initialPageSize: 10, customHeaderContent: _jsxs("div", { className: 'flex flex-row items-end gap-3', children: [_jsx(SearchField, { ariaLabel: 'Search', autoComplete: 'off', value: searchTerm.value, onChange: (event) => handleSearchChange(event.target.value), onClear: handleSearchClear, onSearch: handleSearchChange, placeholder: translate('generic.search'), width: 'base' }), _jsx(Select, { options: userTypeOptions, onChange: handleSelectUserType, label: translate('label.participant_responses.user_type'), value: selectedUserType && 'value' in selectedUserType ? selectedUserType : undefined, width: 'sm' }, 'userType'), _jsx(DateRangeSelector, { value: selectedDateRange, onChange: handleDateRangeChange }), _jsx(Button, { variant: 'transparent', size: 'base', onClick: handleClearFilters, disabled: isResetFiltersDisabled, "aria-label": 'Clear filters', label: translate('label.date_range_selector.clear') })] }) }) }));
};
